import React, { useState, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { createJoybaseContact, ContactCategory } from "@/services/customerService";
import { Helmet } from 'react-helmet-async';
import MarketingLayout from "./MarketingLayout";
import { generateBreadcrumbJsonLd } from "./utils/breadcrumbJsonLd";
import { Mail, CheckCircle, HelpCircle, Send, Phone, Clock, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";

const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY || "";

const CATEGORY_OPTIONS: { value: ContactCategory; label: string; color: string }[] = [
  { value: 'onboarding', label: '導入について', color: 'bg-yellow-400' },
  { value: 'general', label: '一般的なお問い合わせ', color: 'bg-blue-400' },
  { value: 'technical', label: '技術的な問題', color: 'bg-red-400' },
  { value: 'billing', label: '請求・支払い', color: 'bg-green-400' },
  { value: 'account', label: 'アカウント関連', color: 'bg-purple-400' },
  { value: 'feature_request', label: '機能リクエスト', color: 'bg-pink-400' },
  { value: 'bug_report', label: 'バグ報告', color: 'bg-orange-400' },
  { value: 'other', label: 'その他', color: 'bg-gray-400' },
];

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: "easeOut" }
  }
};

// Custom hook for scroll animation
function useScrollAnimation() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  return { ref, isInView };
}

const ContactPage: React.FC = () => {
  const [category, setCategory] = useState<ContactCategory>('onboarding');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [contactNumber, setContactNumber] = useState<string>('');
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const heroAnimation = useScrollAnimation();
  const formAnimation = useScrollAnimation();
  const faqAnimation = useScrollAnimation();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    if (RECAPTCHA_SITE_KEY && !recaptchaToken) {
      setError("reCAPTCHAにチェックを入れてください");
      return;
    }

    setIsSubmitting(true);

    const formData = {
      name: (e.currentTarget.elements.namedItem('name') as HTMLInputElement).value,
      email: (e.currentTarget.elements.namedItem('email') as HTMLInputElement).value,
      phone: (e.currentTarget.elements.namedItem('phone') as HTMLInputElement).value || undefined,
      category,
      subject: (e.currentTarget.elements.namedItem('subject') as HTMLInputElement).value,
      message: (e.currentTarget.elements.namedItem('message') as HTMLTextAreaElement).value,
    };

    try {
      const response = await createJoybaseContact(formData, recaptchaToken || undefined);
      setContactNumber(response.contact_number);
      setRecaptchaToken(null);
      setIsSuccess(true);
      (e.target as HTMLFormElement).reset();
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.errors?.join(', ') || 'お問い合わせの送信に失敗しました。');
      recaptchaRef.current?.reset();
      setRecaptchaToken(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <>
        <Helmet>
          <title>お問い合わせ完了 | JOYBASE</title>
          <meta property="og:title" content="お問い合わせ完了 | JOYBASE" />
        </Helmet>

        <MarketingLayout>
          <div className="min-h-screen bg-[#FFFEF5] pt-24 pb-16">
            <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                className="bg-white border-4 border-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] p-8 text-center"
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                  className="w-20 h-20 bg-green-400 border-4 border-black rounded-full flex items-center justify-center mx-auto mb-6"
                >
                  <CheckCircle className="w-10 h-10 text-black" />
                </motion.div>

                <h1 className="text-3xl font-black mb-4">お問い合わせを受け付けました</h1>

                <div className="bg-yellow-100 border-4 border-black p-4 mb-6">
                  <p className="text-sm text-gray-600 mb-1">お問い合わせ番号</p>
                  <p className="font-mono font-black text-2xl">{contactNumber}</p>
                </div>

                <p className="text-gray-700 mb-8">
                  担当者より<span className="font-black">24時間以内</span>にメールでご連絡いたします。
                </p>

                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <motion.button
                    whileHover={{ scale: 1.02, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      setIsSuccess(false);
                      setCategory('general');
                    }}
                    className="px-6 py-3 bg-yellow-400 border-4 border-black font-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] transition-all"
                  >
                    別のお問い合わせを送信
                  </motion.button>

                  <Link to="/faq">
                    <motion.button
                      whileHover={{ scale: 1.02, y: -2 }}
                      whileTap={{ scale: 0.98 }}
                      className="w-full sm:w-auto px-6 py-3 bg-blue-400 border-4 border-black font-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] transition-all flex items-center justify-center gap-2"
                    >
                      <HelpCircle className="w-5 h-5" />
                      よくある質問を見る
                    </motion.button>
                  </Link>
                </div>
              </motion.div>
            </div>
          </div>
        </MarketingLayout>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>お問い合わせ | JOYBASE</title>
        <meta property="og:title" content="お問い合わせ | JOYBASE" />
        <meta name="description" content="JOYBASEへのお問い合わせフォーム。導入相談、技術的な問題、機能リクエストなど、お気軽にご連絡ください。" />
        <script type="application/ld+json">
        {generateBreadcrumbJsonLd([
          { name: "ホーム", url: "https://www.joybase.jp/" },
          { name: "お問い合わせ", url: "https://www.joybase.jp/contact" }
        ])}
        </script>
      </Helmet>

      <MarketingLayout>
        <div className="min-h-screen bg-[#FFFEF5]">
          {/* Hero Section */}
          <section className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
            <motion.div
              ref={heroAnimation.ref}
              initial="hidden"
              animate={heroAnimation.isInView ? "visible" : "hidden"}
              variants={containerVariants}
              className="max-w-4xl mx-auto text-center"
            >
              <motion.div
                variants={itemVariants}
                className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-400 border-4 border-black font-black mb-6"
              >
                <Mail className="w-5 h-5" />
                CONTACT US
              </motion.div>

              <motion.h1
                variants={itemVariants}
                className="text-4xl sm:text-5xl font-black mb-6"
              >
                お問い合わせ
              </motion.h1>

              <motion.p
                variants={itemVariants}
                className="text-lg text-gray-700 max-w-2xl mx-auto"
              >
                ご質問やお困りのことがございましたら、下記フォームよりお気軽にお問い合わせください。
              </motion.p>
            </motion.div>
          </section>

          {/* Contact Info Cards */}
          <section className="pb-8 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { icon: Mail, label: "メール対応", value: "24時間受付", color: "bg-blue-400" },
                  { icon: Clock, label: "返信目安", value: "24時間以内", color: "bg-green-400" },
                  { icon: Phone, label: "対応時間", value: "平日 10:00-18:00", color: "bg-purple-400" },
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 + index * 0.1 }}
                    className={`${item.color} border-4 border-black p-4 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]`}
                  >
                    <item.icon className="w-6 h-6 mb-2" />
                    <p className="text-sm font-bold">{item.label}</p>
                    <p className="font-black">{item.value}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* Form Section */}
          <section className="pb-16 px-4 sm:px-6 lg:px-8">
            <motion.div
              ref={formAnimation.ref}
              initial="hidden"
              animate={formAnimation.isInView ? "visible" : "hidden"}
              variants={containerVariants}
              className="max-w-2xl mx-auto"
            >
              <motion.div
                variants={itemVariants}
                className="bg-white border-4 border-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]"
              >
                {/* Form Header */}
                <div className="bg-yellow-400 border-b-4 border-black p-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white border-4 border-black rounded-full flex items-center justify-center">
                      <Send className="w-6 h-6" />
                    </div>
                    <div>
                      <h2 className="text-xl font-black">お問い合わせフォーム</h2>
                      <p className="text-sm">必須項目をご入力ください</p>
                    </div>
                  </div>
                </div>

                {/* Form Body */}
                <div className="p-6 sm:p-8">
                  {error && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mb-6 p-4 bg-red-100 border-4 border-red-500"
                    >
                      <p className="text-red-700 font-bold">{error}</p>
                    </motion.div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-6">
                    <motion.div variants={itemVariants}>
                      <Label htmlFor="name" className="font-black text-base">
                        お名前 <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        placeholder="山田太郎"
                        required
                        className="mt-2 border-4 border-black rounded-none h-12 focus:ring-0 focus:border-yellow-400"
                      />
                    </motion.div>

                    <motion.div variants={itemVariants}>
                      <Label htmlFor="email" className="font-black text-base">
                        メールアドレス <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="example@example.com"
                        required
                        className="mt-2 border-4 border-black rounded-none h-12 focus:ring-0 focus:border-yellow-400"
                      />
                    </motion.div>

                    <motion.div variants={itemVariants}>
                      <Label htmlFor="phone" className="font-black text-base">
                        電話番号（任意）
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        placeholder="090-1234-5678"
                        className="mt-2 border-4 border-black rounded-none h-12 focus:ring-0 focus:border-yellow-400"
                      />
                    </motion.div>

                    <motion.div variants={itemVariants}>
                      <Label htmlFor="category" className="font-black text-base">
                        カテゴリ <span className="text-red-500">*</span>
                      </Label>
                      <Select value={category} onValueChange={(value) => setCategory(value as ContactCategory)}>
                        <SelectTrigger className="mt-2 border-4 border-black rounded-none h-12 focus:ring-0 focus:border-yellow-400">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="border-4 border-black rounded-none">
                          {CATEGORY_OPTIONS.map((option) => (
                            <SelectItem key={option.value} value={option.value} className="font-bold">
                              <span className="flex items-center gap-2">
                                <span className={`w-3 h-3 ${option.color} border-2 border-black`}></span>
                                {option.label}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </motion.div>

                    <motion.div variants={itemVariants}>
                      <Label htmlFor="subject" className="font-black text-base">
                        件名 <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="subject"
                        name="subject"
                        type="text"
                        placeholder="お問い合わせの件名"
                        required
                        maxLength={200}
                        className="mt-2 border-4 border-black rounded-none h-12 focus:ring-0 focus:border-yellow-400"
                      />
                    </motion.div>

                    <motion.div variants={itemVariants}>
                      <Label htmlFor="message" className="font-black text-base">
                        お問い合わせ内容 <span className="text-red-500">*</span>
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        placeholder="お問い合わせ内容を詳しくご記入ください"
                        required
                        maxLength={5000}
                        rows={8}
                        className="mt-2 border-4 border-black rounded-none focus:ring-0 focus:border-yellow-400"
                      />
                      <p className="text-sm text-gray-500 mt-2 font-bold">最大5000文字</p>
                    </motion.div>

                    <motion.div variants={itemVariants}>
                      {RECAPTCHA_SITE_KEY && (
                        <div className="flex justify-center mb-6">
                          <ReCAPTCHA
                            ref={recaptchaRef}
                            sitekey={RECAPTCHA_SITE_KEY}
                            onChange={setRecaptchaToken}
                          />
                        </div>
                      )}

                      <motion.button
                        type="submit"
                        disabled={isSubmitting}
                        whileHover={{ scale: 1.02, y: -2 }}
                        whileTap={{ scale: 0.98 }}
                        className="w-full py-4 bg-yellow-400 border-4 border-black font-black text-lg shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] hover:shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[3px] hover:translate-y-[3px] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {isSubmitting ? (
                          <>
                            <div className="w-5 h-5 border-4 border-black border-t-transparent rounded-full animate-spin"></div>
                            送信中...
                          </>
                        ) : (
                          <>
                            <Send className="w-5 h-5" />
                            送信する
                          </>
                        )}
                      </motion.button>
                    </motion.div>
                  </form>
                </div>
              </motion.div>
            </motion.div>
          </section>

          {/* FAQ Section */}
          <section className="pb-16 px-4 sm:px-6 lg:px-8">
            <motion.div
              ref={faqAnimation.ref}
              initial="hidden"
              animate={faqAnimation.isInView ? "visible" : "hidden"}
              variants={containerVariants}
              className="max-w-2xl mx-auto"
            >
              <motion.div
                variants={itemVariants}
                className="bg-blue-400 border-4 border-black shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] p-8 text-center"
              >
                <div className="w-16 h-16 bg-white border-4 border-black rounded-full flex items-center justify-center mx-auto mb-4">
                  <HelpCircle className="w-8 h-8" />
                </div>
                <h3 className="text-2xl font-black mb-2">お問い合わせの前に</h3>
                <p className="text-gray-800 mb-6">
                  よくある質問で解決できる場合があります。<br />
                  まずはFAQをご確認ください。
                </p>
                <Link to="/faq">
                  <motion.button
                    whileHover={{ scale: 1.02, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    className="inline-flex items-center gap-2 px-8 py-4 bg-white border-4 border-black font-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] transition-all"
                  >
                    よくある質問を見る
                    <ArrowRight className="w-5 h-5" />
                  </motion.button>
                </Link>
              </motion.div>
            </motion.div>
          </section>

          {/* CTA Section */}
          <section className="bg-black text-white py-16 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <h2 className="text-3xl sm:text-4xl font-black mb-4">
                  オリパ販売を始めませんか？
                </h2>
                <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
                  初期費用・月額費用0円でスタート。<br />
                  導入についてのご相談もお気軽にどうぞ。
                </p>
                <Link to="https://dashboard.joybase.jp/signup">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="px-8 py-4 bg-yellow-400 text-black border-4 border-yellow-400 font-black text-lg hover:bg-yellow-300 transition-colors"
                  >
                    無料で始める
                  </motion.button>
                </Link>
              </motion.div>
            </div>
          </section>
        </div>
      </MarketingLayout>
    </>
  );
};

export default ContactPage;
